import { AuthenticatorUser, isAuth0Authenticated } from '@futrli/shared-services';
import { CurrentUserProfile, currentUserQueryKey } from '@futrli/views/queries/currentUser';
import { queryClient } from '@futrli/views/core/queryClient';

import { AuthStoreState } from '../core/types';
import { CUSTOM_ATTRIBUTES } from './constants';

/**
 * Checks if an user is authed.
 * @function checkIfAuthed
 * @param {Object} authReducers
 * @returns {Boolean}
 */
export const checkIfAuthenticated = (authReducers: AuthStoreState) => {
  return Boolean(authReducers.authenticatedUser) || isAuth0Authenticated();
};

/**
 * Checks that an SDK-authenticated user matches the cached backend profile.
 * This verifies session consistency, not authentication; the SDK remains authoritative.
 */
export const matchesCurrentUserProfile = (user: AuthenticatorUser): boolean => {
  const authenticatedUserId = user?.attributes?.sub;
  const profile = queryClient.getQueryData<CurrentUserProfile>(currentUserQueryKey());

  return Boolean(authenticatedUserId && profile?.id === authenticatedUserId);
};

/**
 * Watches for current-user query removal during an authentication attempt.
 * Once reset, the flag stays set even if queries are recreated. Call unsubscribe when the attempt ends.
 */
export const watchCurrentUserCacheReset = () => {
  let hasBeenReset = false;
  const unsubscribe = queryClient.getQueryCache().subscribe(event => {
    // Logout and account changes remove these queries. An ordinary refetch does not.
    if (event.type === 'removed' && event.query.queryKey[0] === currentUserQueryKey()[0]) {
      hasBeenReset = true;
    }
  });

  return {
    hasBeenReset: () => hasBeenReset,
    unsubscribe
  };
};

/**
 * Gets the custom domain where the user registered in order to redirect in the confirmation email.
 * @return {object} custom signUp domain
 */
export const getCustomDomain = () => ({
  [CUSTOM_ATTRIBUTES.SIGNUP_DOMAIN]: `${location.protocol}//${location.host}`
});

export const getCustomBusinessPhoneNumber = (phoneNumber: string) => ({
  [CUSTOM_ATTRIBUTES.BUSINESS_PHONE_NUMBER]: phoneNumber
});

export const getCustomBusinessType = (businessType: string) => ({
  [CUSTOM_ATTRIBUTES.BUSINESS_TYPE]: businessType
});
